import type { FormItem } from 'types';
import type { ModulesAndAgendasState } from '../index';
import type { LocalFormItemTab } from 'types/agendaForm.ts';
import { createEmptyFormItemTab, createNewSection, findSectionIndex, findTabIndex } from 'utils/section/tabSection.ts';

export interface Payload {
  value: {
    gridSizeX: number;
    gridSizeY: number;
    items: FormItem[];
    name: string | null;
    description: string | null;
    collapsed: boolean | null;
  };
  currentTab: number;
  newTab: boolean;
  focusedSectionName: string | null;
  newSubTab: boolean;
}

export const isSectionNameUnique = (tabSections: ModulesAndAgendasState['tabSections'], name: string): boolean => {
  return !tabSections.some((tab) => tab.sections.some((section) => section.name === name));
};

export const isFormItemTabNameUnique = (formItemTabs: LocalFormItemTab[], name: string): boolean => {
  return !formItemTabs.some((tab) => tab.tab.name === name);
};

export const addNewSection = (state: ModulesAndAgendasState, { payload }: { payload: Payload }) => {
  const { value, currentTab, newTab, newSubTab, focusedSectionName } = payload;
  const tabSections = state.tabSections;

  if (!isSectionNameUnique(tabSections, value.name ?? '')) {
    throw new Error('exists');
  }

  const newSection = createNewSection(value.name, value.gridSizeX, value.gridSizeY, value.description ?? null, value.collapsed ?? false);

  if (newTab) {
    state.tabSections.push({
      sections: [newSection],
      tab: { number: tabSections.length },
    });
  } else if (newSubTab) {
    const currentTabIndex = findTabIndex(tabSections, currentTab);
    const currentSection = findSectionIndex(tabSections[currentTabIndex].sections, focusedSectionName);
    const section = tabSections[currentTabIndex].sections[currentSection];
    const formItemTabs = section.formItemTabs;

    if (!isFormItemTabNameUnique(formItemTabs, value.name ?? '')) {
      throw new Error('exists');
    }

    const maxTabNumber = formItemTabs.reduce((max, tab) => Math.max(max, tab.tab.number), -1) + 1;
    const gridSizeX = section.gridSizeX;
    const gridSizeY = section.gridSizeY;

    const newFormItemTab = {
      ...createEmptyFormItemTab(value.name ?? null, { gridSizeX, gridSizeY }),
      tab: {
        number: maxTabNumber,
        name: value.name ?? null,
        description: value.description ?? null,
      },
    } satisfies LocalFormItemTab;

    state.tabSections[currentTabIndex].sections[currentSection].formItemTabs.push(newFormItemTab);
  } else {
    const currentTabIndex = findTabIndex(tabSections, currentTab);
    state.tabSections[currentTabIndex].sections.push(newSection);
  }
};
